import { Box, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import logo from "../../assets/sorisori-logo.png";

const COLUMNS = [
  {
    title: "Product",
    links: [
      { label: "Products", to: "/products" },
      {
        label: "Get Started",
        href: "https://app.sorisori.ai/",
      },
    ],
  },
  {
    title: "Legal",
    links: [
      {
        label: "Privacy Policy",
        href: "/privacy-policy",
      },
    ],
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#0a0a0a",
    borderTop: "1px solid #161616",
    padding: "64px 100px 40px",
    "@media (max-width:600px)": { padding: "48px 24px 32px" },
  },
  inner: { maxWidth: 1280, margin: "0 auto" },
  top: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 64,
    paddingBottom: 48,
    "@media (max-width:900px)": {
      gridTemplateColumns: "1fr",
      gap: 32,
      paddingBottom: 32,
    },
  },
  brandRow: { display: "flex", alignItems: "center", gap: 12 },
  brandIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  brandLogo: {
    width: 24,
    height: 24,
    objectFit: "contain",
    display: "block",
  },
  brandName: {
    fontSize: 15,
    fontWeight: 700,
    color: "#fff",
  },
  tagline: {
    fontSize: 13,
    color: "#444",
    lineHeight: 1.6,
    marginTop: 16,
    maxWidth: 320,
  },
  linkGrid: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 96,
    "@media (max-width:600px)": { gap: 32, justifyContent: "flex-start" },
  },
  colTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "0.5px",
    marginBottom: 16,
  },
  link: {
    display: "block",
    fontSize: 13,
    color: "#444",
    textDecoration: "none",
    padding: "6px 0",
    transition: "color .15s",
    cursor: "pointer",
    "&:hover": { color: "#888" },
  },
  bottom: {
    borderTop: "1px solid #161616",
    paddingTop: 24,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  small: {
    fontSize: 12,
    color: "#333",
  },
  socials: {
    display: "flex",
    gap: 8,
  },
  socialIcon: {
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#444",
    textDecoration: "none",
    borderRadius: 8,
    transition: "color .15s, background .15s",
    "&:hover": {
      color: "#fff",
      background: "rgba(255,255,255,0.06)",
    },
  },
}));

export default function Footer() {
  const classes = useStyles();
  return (
    <Box component="footer" className={classes.root}>
      <Box className={classes.inner}>
        <Box className={classes.top}>
          <Box>
            <Box className={classes.brandRow}>
              <Box className={classes.brandIcon}>
                <img
                  src={logo}
                  alt="SoriSori AI"
                  className={classes.brandLogo}
                />
              </Box>
              <Typography className={classes.brandName}>SoriSori AI</Typography>
            </Box>
            <Typography className={classes.tagline}>
              All-in-One AI Content Studio
            </Typography>
          </Box>

          <Box className={classes.linkGrid}>
            {COLUMNS.map((col) => (
              <Box key={col.title}>
                <Typography className={classes.colTitle}>
                  {col.title}
                </Typography>
                {col.links.map((link) => {
                  const isExternalLink =
                    link.href && /^https?:\/\//.test(link.href);

                  return link.href ? (
                    <Box
                      component="a"
                      key={link.label}
                      href={link.href}
                      target={isExternalLink ? "_blank" : undefined}
                      rel={isExternalLink ? "noopener noreferrer" : undefined}
                      className={classes.link}
                    >
                      {link.label}
                    </Box>
                  ) : (
                    <Box
                      component={RouterLink}
                      key={link.label}
                      to={link.to}
                      className={classes.link}
                    >
                      {link.label}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={classes.bottom}>
          <Typography className={classes.small}>
            © 2026 SoriSori AI. All rights reserved.
          </Typography>
          <Stack direction="row" className={classes.socials}>
            <Box
              component="a"
              href="https://www.instagram.com/sorisori.ai/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className={classes.socialIcon}
            >
              <InstagramIcon fontSize="small" />
            </Box>
            <Box
              component="a"
              href="https://www.youtube.com/@ytsorisori"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className={classes.socialIcon}
            >
              <YouTubeIcon fontSize="small" />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
